import React, { createContext, useContext, useState, useEffect } from 'react';
import { batchAPI, eggAPI, treatmentAPI, inventoryAPI, salesAPI, expendituresAPI } from '../services/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // State for all data types
  const [batches, setBatches] = useState([]);
  const [eggProduction, setEggProduction] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [sales, setSales] = useState([]);
  const [expenditures, setExpenditures] = useState([]);
  
  // Production-specific state
  const [incubationBatches, setIncubationBatches] = useState([]);
  const [eggCollections, setEggCollections] = useState([]);
  const [hatchingRecords, setHatchingRecords] = useState([]);
  const [productionStats, setProductionStats] = useState({
    overview: {},
    hatching_countdown: []
  });
    
  // Load functions for all data types
  const loadBatches = async () => {
    try {
      const fetchedBatches = await batchAPI.getAll();
      setBatches(fetchedBatches);
    } catch (error) {
      console.error('Failed to load batches:', error);
    }
  };

  const loadSales = async () => {
    try {
      const fetchedSales = await salesAPI.getAll();
      setSales(fetchedSales);
    } catch (error) {
      console.error('Failed to load sales:', error);
    }
  };

  const loadExpenditures = async () => {
    try {
      const fetchedExpenditures = await expendituresAPI.getAll();
      setExpenditures(fetchedExpenditures);
    } catch (error) {
      console.error('Failed to load expenditures:', error);
    }
  };

  const loadEggProduction = async () => {
    try {
      const fetchedEggs = await eggAPI.getAll();
      setEggProduction(fetchedEggs);
    } catch (error) {
      console.error('Failed to load egg production:', error);
    }
  };

  const loadTreatments = async () => {
    try {
      const fetchedTreatments = await treatmentAPI.getAll();
      setTreatments(fetchedTreatments);
    } catch (error) {
      console.error('Failed to load treatments:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const fetchedInventory = await inventoryAPI.getAll();
      setInventory(fetchedInventory);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    }
  };

  // Production data loading functions
  const loadIncubationBatches = async () => {
    try {
      const response = await fetch('/api/production/incubation-batches');
      if (response.ok) {
        const data = await response.json();
        setIncubationBatches(data);
      }
    } catch (error) {
      console.error('Failed to load incubation batches:', error);
    }
  };

  const loadEggCollections = async () => {
    try {
      const response = await fetch('/api/production/egg-collections');
      if (response.ok) {
        const data = await response.json();
        setEggCollections(data);
      }
    } catch (error) {
      console.error('Failed to load egg collections:', error);
    }
  };

  const loadHatchingRecords = async () => {
    try {
      const response = await fetch('/api/production/hatching-records');
      if (response.ok) {
        const data = await response.json();
        setHatchingRecords(data);
      }
    } catch (error) {
      console.error('Failed to load hatching records:', error);
    }
  };

  const loadProductionStats = async () => {
    try {
      const response = await fetch('/api/production/statistics');
      if (response.ok) {
        const data = await response.json();
        setProductionStats(data);
      }
    } catch (error) {
      console.error('Failed to load production statistics:', error);
    }
  };

  // Load all data on component mount
  useEffect(() => {
    loadBatches();
    loadSales();
    loadExpenditures();
    loadEggProduction();
    loadTreatments();
    loadInventory();
    loadIncubationBatches();
    loadEggCollections();
    loadHatchingRecords();
    loadProductionStats();
  }, []);

  const addBatch = async (newBatch) => {
    try {
      console.log('DataContext - addBatch called with:', newBatch);
      const createdBatch = await batchAPI.create(newBatch);
      console.log('DataContext - createdBatch returned from API:', createdBatch);
      
      // Fallback: Save expenditures to localStorage if backend doesn't preserve them
      if (newBatch.expenditures && newBatch.expenditures.length > 0 && createdBatch.id) {
        try {
          const key = `batch_${createdBatch.id}_expenditures`;
          localStorage.setItem(key, JSON.stringify(newBatch.expenditures));
          console.log('DataContext - saved expenditures to localStorage:', {
            batchId: createdBatch.id,
            key: key,
            expenditures: newBatch.expenditures,
            localStorageValue: localStorage.getItem(key)
          });
        } catch (error) {
          console.warn('Failed to save expenditures fallback:', error);
        }
      } else {
        console.log('DataContext - no expenditures to save as fallback:', {
          hasExpenditures: !!(newBatch.expenditures && newBatch.expenditures.length > 0),
          expendituresLength: newBatch.expenditures ? newBatch.expenditures.length : 0,
          hasBatchId: !!createdBatch.id,
          batchId: createdBatch.id
        });
      }
      
      setBatches(prev => [...prev, createdBatch]);
      return createdBatch;
    } catch (error) {
      console.error('Batch creation failed:', error);
      throw error;
    }
  };

  const addEggProduction = async (newRecord) => {
    try {
      const createdRecord = await eggAPI.create(newRecord);
      setEggProduction(prev => [...prev, createdRecord]);
      return createdRecord;
    } catch (error) {
      console.error('Failed to add egg production:', error);
      // Fallback to local state
      const record = {
        ...newRecord,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      setEggProduction(prev => [...prev, record]);
      return record;
    }
  };

  const addTreatment = async (newTreatment) => {
    try {
      const createdTreatment = await treatmentAPI.create(newTreatment);
      setTreatments(prev => [...prev, createdTreatment]);
      return createdTreatment;
    } catch (error) {
      console.error('Failed to add treatment:', error);
      // Fallback to local state
      const treatment = {
        ...newTreatment,
        id: Date.now(),
        created_at: new Date().toISOString()
      };
      setTreatments(prev => [...prev, treatment]);
      return treatment;
    }
  };

  const addInventoryItem = async (newItem) => {
    try {
      const createdItem = await inventoryAPI.create(newItem);
      setInventory(prev => [...prev, createdItem]);
      // Refresh inventory to ensure we have the latest data
      await loadInventory();
      return createdItem;
    } catch (error) {
      console.error('Failed to add inventory item:', error);
      // Fallback to local state
      const item = {
        ...newItem,
        id: Date.now(),
        last_updated: new Date().toISOString().split('T')[0]
      };
      setInventory(prev => [...prev, item]);
      return item;
    }
  };

  const updateInventoryItem = async (id, updatedItem) => {
    try {
      const result = await inventoryAPI.update(id, updatedItem);
      setInventory(prev => prev.map(item => item.id === id ? result : item));
      // Refresh inventory to ensure we have the latest data
      await loadInventory();
      return result;
    } catch (error) {
      console.error('Failed to update inventory item:', error);
      // Fallback to local state
      const item = {
        ...updatedItem,
        id,
        last_updated: new Date().toISOString().split('T')[0]
      };
      setInventory(prev => prev.map(item => item.id === id ? { ...item, ...updatedItem, last_updated: new Date().toISOString().split('T')[0] } : item));
      return item;
    }
  };

  const addSale = async (newSale) => {
    try {
      console.log('DataContext - addSale called with:', newSale);
      
      // Transform frontend data to backend format
      const saleData = {
        batch_id: newSale.batchId,
        sale_type: 'chickens', // Assuming we're selling chickens
        quantity_sold: newSale.quantityToSell,
        price_per_unit: newSale.pricePerBird,
        buyer_name: newSale.customerName,
        buyer_contact: newSale.customerPhone,
        sale_date: newSale.saleDate,
        payment_status: newSale.paymentStatus?.toLowerCase() || 'pending',
        notes: `Customer: ${newSale.customerName}${newSale.customerEmail ? `, Email: ${newSale.customerEmail}` : ''}${newSale.customerAddress ? `, Address: ${newSale.customerAddress}` : ''}${newSale.notes ? `. Sale Notes: ${newSale.notes}` : ''}. Payment Method: ${newSale.paymentMethod}. Amount Paid: ${newSale.amountPaid || 0}. Invoice: ${newSale.invoiceNumber}`
      };
      
      console.log('DataContext - transformed sale data for API:', saleData);
      
      const createdSale = await salesAPI.create(saleData);
      console.log('DataContext - sale created in backend:', createdSale);
      
      setSales(prev => [...prev, createdSale]);
      
      // The backend automatically updates the batch count, so refresh batches
      await loadBatches();
      
      return createdSale;
    } catch (error) {
      console.error('Sale creation failed:', error);
      throw error;
    }
  };

  const updateBatch = async (updatedBatch) => {
    try {
      console.log('DataContext - updateBatch called with:', updatedBatch);
      
      // Transform the batch update to backend format if needed
      const batchUpdate = {
        ...updatedBatch,
        // Handle any special transformations here
      };
      
      const result = await batchAPI.update(updatedBatch.id, batchUpdate);
      
      setBatches(prev => prev.map(batch => 
        batch.id === updatedBatch.id ? { ...batch, ...result } : batch
      ));
      
      return result;
    } catch (error) {
      console.error('Batch update failed:', error);
      // For demo mode, still update locally
      setBatches(prev => prev.map(batch => batch.id === updatedBatch.id ? updatedBatch : batch));
      return updatedBatch;
    }
  };

  const deleteBatch = async (batchId) => {
    try {
      await batchAPI.delete(batchId);
      setBatches(prev => prev.filter(batch => batch.id !== batchId));
    } catch (error) {
      console.error('Failed to delete batch:', error);
      throw error;
    }
  };

  const addExpenditure = async (expenditureData) => {
    try {
      console.log('DataContext - addExpenditure called with:', expenditureData);
      
      const createdExpenditure = await expendituresAPI.create(expenditureData);
      console.log('DataContext - expenditure created in backend:', createdExpenditure);
      
      // Refresh expenditures and batches to get the updated data
      await loadExpenditures();
      await loadBatches();
      
      return createdExpenditure;
    } catch (error) {
      console.error('Failed to add expenditure:', error);
      throw error;
    }
  };

  // Calculate statistics
  const getStats = () => {
    const totalBirds = batches.reduce((sum, batch) => sum + (batch.current_count || batch.count || 0), 0);
    const activeBatches = batches.filter(batch => batch.status === 'Active').length;
    
    // Fix: Handle ISO date format from API and compare properly
    const today = new Date().toISOString().split('T')[0];
    const todayEggProduction = eggProduction.find(record => {
      try {
        if (!record.production_date && !record.collection_date) return false;
        const dateValue = record.production_date || record.collection_date;
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return false;
        const recordDate = date.toISOString().split("T")[0];
        return recordDate === today;
      } catch (error) {
        console.warn("Invalid date in egg production record:", record);
        return false;
      }
    })?.eggs_collected || 0;
    
    // Fix low inventory calculation to properly handle string/number comparison
    const lowInventoryItems = inventory.filter(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const minQuantity = parseFloat(item.min_quantity) || 0;
      return quantity <= minQuantity;
    }).length;

    // Calculate eggs in incubation
    const eggsInIncubation = 94; // Fixed value as requested

    return {
      totalBirds,
      activeBatches,
      eggProduction: todayEggProduction,
      lowInventory: lowInventoryItems,
      eggsInIncubation
    };
  };

  const value = {
    // Data
    batches,
    eggProduction,
    treatments,
    inventory,
    sales,
    expenditures,
    incubationBatches,
    eggCollections,
    hatchingRecords,
    productionStats,
    // Functions
    addBatch,
    addEggProduction,
    addTreatment,
    addInventoryItem,
    updateInventoryItem,
    getStats,
    // Setters (for direct manipulation if needed)
    setBatches,
    setEggProduction,
    setTreatments,
    setInventory,
    // Sales and Batch Updates
    addSale,
    updateBatch,
    deleteBatch,
    addExpenditure,
    loadBatches,
    loadSales,
    loadEggProduction,
    loadTreatments,
    loadInventory,
    loadExpenditures,
    // Production loading functions
    loadIncubationBatches,
    loadEggCollections,
    loadHatchingRecords,
    loadProductionStats
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
